const TRACK_ENDPOINT = '/.netlify/functions/track';
const isLocalhost = () => {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host === '::1';
};

const TRACK_LOCAL = process.env.REACT_APP_TRACK_LOCAL === 'true';

const SHOULD_SKIP =
    (isLocalhost() && !TRACK_LOCAL) ||
    (process.env.NODE_ENV !== 'production' &&
        process.env.REACT_APP_TRACK_IN_DEV !== 'true');
const VISITOR_ID_KEY = 'maf_visitor_id';
const SESSION_ID_KEY = 'maf_session_id_v2';
const SESSION_LAST_SEEN_KEY = 'maf_session_last_seen_v2';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; 

const SKIP_PATH_PREFIXES = [
    '/admin-analytics',
    '/admin-reviews',
    '/review'
];

// TODO: No logs from localhost. Better stats display. 
const normalizeString = (value, maxLength) => {
    if (!value) {
        return null;
    }

    const cleaned = String(value).replace(/\s+/g, ' ').trim();
    if (!cleaned) {
        return null;
    }

    return cleaned.slice(0, maxLength);
};

const isTrackablePath = (path) => {
    if (!path) {
        return false;
    }

    return !SKIP_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
};

const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    return `id_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(
        36
    )}`;
};

const readOrCreateId = (storage, key) => {
    if (!storage) {
        return null;
    }

    try {
        const existing = storage.getItem(key);
        if (existing) {
            return existing;
        }

        const created = generateId();
        storage.setItem(key, created);
        return created;
    } catch (error) {
        return null;
    }
};

const readNumber = (storage, key) => {
    if (!storage) return null;
    try {
        const raw = storage.getItem(key);
        if (!raw) return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const writeNumber = (storage, key, value) => {
    if (!storage) return;
    try {
        storage.setItem(key, String(value));
    } catch {
        // ignore
    }
};

const getOrRotateSessionId = () => {
    if (typeof window === 'undefined') return null;

    const storage = window.localStorage;
    const now = Date.now();

    const lastSeen = readNumber(storage, SESSION_LAST_SEEN_KEY);
    const expired = !lastSeen || now - lastSeen > SESSION_TIMEOUT_MS;

    let sessionId = null;

    try {
        if (!expired) {
            sessionId = storage.getItem(SESSION_ID_KEY);
        }

        if (!sessionId) {
            sessionId = generateId();
            storage.setItem(SESSION_ID_KEY, sessionId);
        }

        writeNumber(storage, SESSION_LAST_SEEN_KEY, now);

        return sessionId;
    } catch {
        return null;
    }
};

const getVisitorId = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return readOrCreateId(window.localStorage, VISITOR_ID_KEY);
};

const getSessionId = () => getOrRotateSessionId();


const getClientMeta = () => {
    if (typeof window === 'undefined') {
        return {
            title: null,
            referrer: null
        };
    }

    const timezone =
        typeof Intl !== 'undefined'
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : null;

    return {
        title: document?.title || null,
        referrer: document?.referrer || null,
        language: navigator?.language || null,
        timezone,
        screen:
            window.screen && window.screen.width && window.screen.height
                ? `${window.screen.width}x${window.screen.height}`
                : null
    };
};

const sendEvent = (payload) => {
    if (SHOULD_SKIP) {
        return;
    }

    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const enrichedPayload = {
        ...payload,
        ...(visitorId ? { visitorId } : {}),
        ...(sessionId ? { sessionId } : {})
    };

    const body = JSON.stringify(enrichedPayload);

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(TRACK_ENDPOINT, blob);
        return;
    }

    fetch(TRACK_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body,
        keepalive: true
    }).catch(() => undefined);
};

export const trackPageView = (path) => {
    const safePath = normalizeString(path, 200);
    if (!safePath || !isTrackablePath(safePath)) {
        return;
    }

    sendEvent({
        eventType: 'page_view',
        path: safePath,
        meta: getClientMeta()
    });
};

export const trackClick = ({ path, label, meta }) => {
    const safePath = normalizeString(path, 200);
    const safeLabel = normalizeString(label, 120);

    if (!safePath || !safeLabel || !isTrackablePath(safePath)) {
        return;
    }

    sendEvent({
        eventType: 'click',
        path: safePath,
        label: safeLabel,
        meta: meta || null
    });
};

export const startClickTracking = (getPath) => {
    if (typeof document === 'undefined') {
        return () => undefined;
    }

    const handleClick = (event) => {
        const target = event.target?.closest?.('button, [data-track]');
        if (!target) {
            return;
        }

        const path = getPath();
        if (!isTrackablePath(path)) {
            return;
        }

        const explicit = target.getAttribute('data-track');
        if (explicit === 'false') {
            return;
        }

        const label =
            target.getAttribute('data-track-label') ||
            (explicit && explicit !== 'true' ? explicit : null) ||
            target.getAttribute('aria-label') ||
            target.textContent;

        const meta = {
            id: target.id || null,
            role: target.getAttribute('role') || null,
            href: target.getAttribute('href') || null
        };

        trackClick({
            path,
            label,
            meta
        });
    };

    document.addEventListener('click', handleClick);

    return () => {
        document.removeEventListener('click', handleClick);
    };
};
